import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsBoxHelper,
  HsColor,
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicLines,
  HsGraphicObject,
  HsGraphicPoints,
  HsMatrix4,
  HsPointsMaterial,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
  type HsShader,
  type HsLightStorage,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import {
  makeCustomFragmentMaterial,
  makeSkyDome,
  placeSkyDome,
  setShaderTime,
} from '../../functions/custom-shader-material.function';
import { makeSdfPointCloudData, type SignedDistanceFunction } from '../../functions/sdf-point-cloud.function';
import { makeLitMaterial } from '../../functions/make-lit-material.function';
import { makePinkPointLights } from '../../functions/make-pink-point-lights.function';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [2, 10, 30];
const GROUND_SIZE: number = 40;
const COPIES_COUNT: number = 10;
const SPHERE_SDF: SignedDistanceFunction = (x: number, y: number, z: number) => x * x + y * y + z * z - 1;

// Three ray-marched spheres blended with a soft minimum; v_point is the camera-relative position.
const BLOBS_SHADER_CODE: string = `
float softMin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

float getDist(vec3 p) {
  p += vec3(-10.0, -2.5, 0.0);
  vec3 p1 = vec3(sin(u_time * 0.5), sin(u_time * 0.6), sin(u_time * 0.7)) * 2.0;
  vec3 p2 = vec3(sin(u_time * 0.4), sin(u_time * 0.7), sin(u_time * 0.5)) * 2.0;
  vec3 p3 = vec3(sin(u_time * 0.6), sin(u_time * 0.5), sin(u_time * 0.3)) * 2.0;
  float d = length(p - p1) - 1.0;
  d = softMin(d, length(p - p2) - 1.0, 0.5);
  d = softMin(d, length(p - p3) - 1.0, 0.5);
  return d;
}

vec3 getNormal(vec3 p) {
  float d = getDist(p);
  vec2 e = vec2(0.0001, 0.0);
  vec3 n = d - vec3(getDist(p - e.xyy), getDist(p - e.yxy), getDist(p - e.yyx));
  return normalize(n);
}

float march(vec3 ro, vec3 rd) {
  vec3 light = normalize(vec3(-0.5, 1.0, 0.5));
  vec3 p = ro;
  for (int i = 0; i < 200; i++) {
    float d = getDist(p);
    if (d > 20.0) discard;
    if (d < 0.005) {
      vec3 n = getNormal(p);
      vec3 refDir = reflect(rd, n);
      float dif = dot(n, light) * 0.5 + 0.75;
      float ref = dot(light, refDir) * 0.5 + 0.5;
      ref = pow(ref, 256.0);
      return dif + ref;
    }
    p += rd * d;
  }
  discard;
}

void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec4 intersectionPoint = v_point / v_point.w;
  vec3 direction = normalize(intersectionPoint.xyz);
  vec3 color = vec3(march(-v_position.xyz, direction)) * vec3(1.0, 0.5, 0.1);
  fragmentColor = vec4(color, 1.0);
}
`;

/** Line circles around three axes, as the legacy line sphere primitive drew them. */
function makeLineSphereData(segmentsCount: number): HsRegularGeometryData {
  const vertices: number[] = [];
  const circles: ((x: number, z: number) => HsHypersceneTypes.Array3)[] = [
    (x: number, z: number) => [x + 1, 0, z],
    (x: number, z: number) => [x + 1, z, 0],
    (x: number, z: number) => [1, x, z],
  ];
  for (const circle of circles) {
    for (let i: number = 0; i <= segmentsCount; i++) {
      const angle: number = (i / segmentsCount) * Math.PI * 2;
      const point: HsHypersceneTypes.Array3 = circle(Math.cos(angle) * 2, Math.sin(angle) * 2);
      vertices.push(...point);
      // Inner points end one segment and start the next.
      if (i > 0 && i < segmentsCount) {
        vertices.push(...point);
      }
    }
  }
  return new HsRegularGeometryData(vertices, []);
}

/** A checkerboard of instanced cubes. */
function makeCheckerGround(
  graphics: HsGraphics,
  light: HsHypersceneTypes.Array3,
  dark: HsHypersceneTypes.Array3,
  height: number,
  lights: HsLightStorage
): HsGraphicCollection | null {
  const geometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(hsGenerateCubeGeometryData()));
  if (geometry === null) {
    return null;
  }
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
  shaderData.setLightStorage(lights).addColor().addDepth().addNormals().addDiffuseLighting();
  const material: HsMaterial = new HsDefaultMaterial(graphics.gl, shaderData.makeShader());
  const ground: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
  for (let i: number = 0; i < GROUND_SIZE; i++) {
    for (let j: number = 0; j < GROUND_SIZE; j++) {
      const matrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix().setPosition([
        i * 2 - GROUND_SIZE,
        height,
        j * 2 - GROUND_SIZE,
      ]);
      ground.addInstance(matrix.values, (i + j) % 2 === 1 ? dark : light);
    }
  }
  ground.updateCenter();
  ground.updateBoundingSphere();
  ground.updateBuffers(graphics);
  return ground;
}

/**
 * Every kind of object of the engine in one scene: meshes from regular and indexed geometry, an instanced ground,
 * a pulsing point cloud with its bounding box, repeated line and point objects, a ray-marched custom shader and a
 * sky.
 */
@Component({
  selector: 'hyperscene-all-objects',
  templateUrl: './all-objects.component.html',
  styleUrls: ['./all-objects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class AllObjectsDemo extends DemoViewerPage {
  private readonly lights: HsLightStorage = makePinkPointLights();
  private points: HsGraphicPoints | null = null;
  private skyDome: HsGraphicObject | null = null;
  private readonly animatedShaders: HsShader[] = [];
  private readonly startedAt: number = performance.now();

  protected initScene(viewer: HsHypersceneViewer): void {
    const gl: WebGL2RenderingContext = viewer.gl;
    const graphics: HsGraphics = viewer.surface.graphics;
    const cubeGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, hsGenerateCubeGeometryData());
    const indexedCubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(
      graphics,
      hsIndexGeometry(hsGenerateCubeGeometryData())
    );
    const lineSphereGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makeLineSphereData(100));
    const pointsGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makeSdfPointCloudData(20, SPHERE_SDF));
    const smallPointsGeometry: HsRegularGeometry | null = hsMakeGeometry(
      graphics,
      makeSdfPointCloudData(10, SPHERE_SDF)
    );
    if (
      cubeGeometry === null ||
      indexedCubeGeometry === null ||
      lineSphereGeometry === null ||
      pointsGeometry === null ||
      smallPointsGeometry === null
    ) {
      return;
    }

    const pointsShader: HsDefaultShader = new HsShaderData(HsShaderObjectType.single)
      .addColor()
      .addDepth()
      .addPoints()
      .makeShader();
    const pointsMaterial: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(gl, pointsShader);
    this.points = new HsGraphicPoints(getUuid(), 'points', pointsGeometry, pointsMaterial);
    this.points.worldMatrix.setPosition([0, 4, 0]).scale([3.5, 3.5, 3.5]);
    this.points.updateBoundingSphere();
    viewer.add(this.points);

    const boxHelper: HsBoxHelper = new HsBoxHelper(graphics, this.points.boundingBox, HsColor.makeBlack());
    const boxLines: HsGraphicLines = boxHelper.makeLines();
    boxLines.updateBoundingSphere();
    viewer.add(boxLines);

    this.addMesh(viewer, cubeGeometry, makeLitMaterial(gl, 1, 1, 1, this.lights), [-10, 2, 0], 2);
    this.addMesh(viewer, indexedCubeGeometry, makeLitMaterial(gl, 1, 0.75, 0, this.lights), [-15, 2, 0], 2);

    const ground: HsGraphicCollection | null = makeCheckerGround(graphics, [1, 1, 1], [0.3, 0.3, 0.3], -2, this.lights);
    if (ground !== null) {
      viewer.add(ground);
    }

    // The legacy line and point collections become separate objects that share one geometry and material.
    const lineMaterial: HsMaterial = new HsDefaultMaterial(
      gl,
      new HsShaderData(HsShaderObjectType.single).addColor().addDepth().makeShader()
    );
    lineMaterial.color.set(1, 0.5, 0);
    const smallPointsMaterial: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(gl, pointsShader, false);
    for (let i: number = 0; i < COPIES_COUNT; i++) {
      const lines: HsGraphicLines = new HsGraphicLines(getUuid(), 'lineSphere', lineSphereGeometry, lineMaterial);
      lines.worldMatrix.setPosition([i * 5 - 25, 2, -15]);
      lines.updateBoundingSphere();
      viewer.add(lines);

      const points: HsGraphicPoints = new HsGraphicPoints(
        getUuid(),
        'smallPoints',
        smallPointsGeometry,
        smallPointsMaterial
      );
      points.setPointSize(4);
      points.worldMatrix.setPosition([i * 5 - 25, 3, -25]);
      points.updateBoundingSphere();
      viewer.add(points);
    }

    const blobsMaterial: HsMaterial = makeCustomFragmentMaterial(
      gl,
      new HsShaderData(HsShaderObjectType.single).addColor().addDepth().addRayIntersection(),
      BLOBS_SHADER_CODE,
      'in vec4 v_point;'
    );
    this.animatedShaders.push(blobsMaterial.getShader());
    this.addMesh(viewer, cubeGeometry, blobsMaterial, [10, 2, 0], 4);

    this.skyDome = makeSkyDome(graphics);
    if (this.skyDome !== null) {
      viewer.add(this.skyDome);
    }

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
    viewer.camera.updateProjectionMatrix();
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    const seconds: number = (performance.now() - this.startedAt) / 1000;
    this.points?.setPointSize(20 + Math.pow(Math.sin(seconds) * 0.5 + 0.5, 2) * 10);
    setShaderTime(viewer.gl, this.animatedShaders, seconds);
    if (this.skyDome !== null) {
      placeSkyDome(this.skyDome, viewer.camera);
    }
    return false;
  }

  private addMesh(
    viewer: HsHypersceneViewer,
    geometry: HsGeometry,
    material: HsMaterial,
    position: HsHypersceneTypes.Array3,
    scale: number
  ): void {
    const mesh: HsGraphicObject = new HsGraphicObject(getUuid(), 'mesh', geometry, material);
    mesh.worldMatrix.setPosition(position).scale([scale, scale, scale]);
    mesh.updateBoundingSphere();
    viewer.add(mesh);
  }
}
